"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ValidationMode = void 0;
const ConditionField_1 = __importDefault(require("./ConditionField"));
const errors_1 = require("./errors");
var ValidationMode;
(function (ValidationMode) {
    ValidationMode["ALL"] = "ALL";
    ValidationMode["ORIGINATOR"] = "ORIGINATOR";
    ValidationMode["BENEFICIARY"] = "BENEFICIARY";
})(ValidationMode = exports.ValidationMode || (exports.ValidationMode = {}));
const originatorFields = [
    'originatorName',
    'originatorAccountNumber',
    'originatorNationalIdentification',
    'originatorCustomerIdentification',
    'originatorDateAndPlaceOfBirth',
    'originatorGeographicAddress',
    'originatorOwnershipProof',
];
const beneficiaryFields = [
    'beneficiaryName',
    'beneficiaryAccountNumber',
    'beneficiaryNationalIdentification',
    'beneficiaryCustomerIdentification',
    'beneficiaryDateAndPlaceOfBirth',
    'beneficiaryGeographicAddress',
    'beneficiaryOwnershipProof',
];
class ThresholdCondition {
    constructor(fields, type, conditions) {
        this.isOwnershipProofField = (field) => {
            return field.includes('Proof');
        };
        this.fields = fields;
        this.type = type;
        this.conditions = conditions;
    }
    /**
     * Validate an IVMS payload against this condition
     *
     * @param ivms the IVMS payload
     * @param mode the mode of the validation
     * @returns true if valid
     * @throws if invalid
     */
    validate(ivms, mode = ValidationMode.ALL, extraFields) {
        return __awaiter(this, void 0, void 0, function* () {
            const fields = [
                ...this.validateFields(ivms, mode),
                ...this.validateExtraFields(extraFields || {}, mode),
                ...this.validateConditions(ivms, mode),
            ];
            const results = yield Promise.allSettled(fields);
            if (results.length === 0)
                return true;
            const errors = results.filter((r) => r.status === 'rejected');
            if (this.type === 'AND') {
                if (errors.length > 0) {
                    throw new errors_1.MissingFields('AND', errors.map((e) => e.reason));
                }
                return true;
            }
            // It must be an OR condition, so if at least 1 has fulfilled then we can return true
            if (results.filter((r) => r.status === 'fulfilled').length > 0)
                return true;
            throw new errors_1.MissingFields('OR', errors.map((e) => e.reason));
        });
    }
    /**
     * Get a structure of required fields
     *
     * @param mode the validation mode
     * @returns a structure of required fields
     */
    getRequiredFields(mode) {
        var _a, _b;
        const type = (_a = this.type) !== null && _a !== void 0 ? _a : 'AND';
        const fields = this.filterFields(mode).map((f) => f.field);
        const extraFields = this.filterNonCustodialFields(mode).map((f) => f.field);
        const conditions = ((_b = this.conditions) !== null && _b !== void 0 ? _b : []).map((c) => c.getRequiredFields(mode));
        const fieldsAndConditions = [
            ...fields,
            ...extraFields,
            ...conditions,
        ].filter((f) => {
            if (typeof f === 'string')
                return true;
            return f.fields.length > 0;
        });
        return { type, fields: fieldsAndConditions };
    }
    /**
     * Validate the IVMS payload against the field validators
     *
     * @param ivms the IVMS payload
     * @param mode the mode of the validation
     * @returns an array of validations that can be awaited
     */
    validateFields(ivms, mode) {
        return this.filterFields(mode).map((f) => f
            .getValidator()
            .validate(ivms)
            .then(() => true)
            .catch((err) => {
            throw new errors_1.MissingField(`The field ${f.field} is missing`, f.field);
        }));
    }
    /**
     * Validate extra fields against the field validators
     *
     * @param fields extra fields
     * @param mode the mode of the validation
     * @returns an array of validations that can be awaited
     */
    validateExtraFields(fields, mode) {
        return this.filterNonCustodialFields(mode).map((f) => f
            .getExtraFieldValidator()
            .validate(fields[f.field])
            .then(() => true)
            .catch((err) => {
            throw new errors_1.MissingField(`The field ${f.field} is missing`, f.field);
        }));
    }
    /**
     * Valdate the IVMS payload against the nested conditions
     *
     * @param ivms the IVMS payload
     * @param mode the mode of the validation
     * @returns an array of validations that can be awaited
     */
    validateConditions(ivms, mode) {
        var _a;
        return (_a = (this.conditions || [])) === null || _a === void 0 ? void 0 : _a.map((c) => c.validate(ivms, mode));
    }
    /**
     * Filter the field validators based on the mode of validation
     *
     * @param mode the validation mode
     * @returns a filtered list of field validators
     */
    filterFields(mode) {
        var _a, _b;
        const currentFields = (this.fields || []).filter((f) => !this.isOwnershipProofField(f.field));
        switch (mode) {
            case 'ORIGINATOR':
                return ((_a = currentFields === null || currentFields === void 0 ? void 0 : currentFields.filter((field) => originatorFields.includes(field.field))) !== null && _a !== void 0 ? _a : []);
            case 'BENEFICIARY':
                return ((_b = currentFields === null || currentFields === void 0 ? void 0 : currentFields.filter((field) => beneficiaryFields.includes(field.field))) !== null && _b !== void 0 ? _b : []);
            default:
                return currentFields !== null && currentFields !== void 0 ? currentFields : [];
        }
    }
    filterNonCustodialFields(mode) {
        var _a, _b, _c, _d, _e, _f;
        switch (mode) {
            case 'ORIGINATOR':
                return ((_b = (_a = this.fields) === null || _a === void 0 ? void 0 : _a.filter((field) => originatorFields.includes(field.field) &&
                    this.isOwnershipProofField(field.field))) !== null && _b !== void 0 ? _b : []);
            case 'BENEFICIARY':
                return ((_d = (_c = this.fields) === null || _c === void 0 ? void 0 : _c.filter((field) => beneficiaryFields.includes(field.field) &&
                    this.isOwnershipProofField(field.field))) !== null && _d !== void 0 ? _d : []);
            default:
                return ((_f = (_e = this.fields) === null || _e === void 0 ? void 0 : _e.filter((f) => this.isOwnershipProofField(f.field))) !== null && _f !== void 0 ? _f : []);
        }
    }
    static build(condition) {
        var _a, _b;
        return new ThresholdCondition((_a = condition.fields) === null || _a === void 0 ? void 0 : _a.map((f) => ConditionField_1.default.build(f)), condition.type, (_b = condition.conditions) === null || _b === void 0 ? void 0 : _b.map((c) => ThresholdCondition.build(c)));
    }
}
exports.default = ThresholdCondition;
